import React, { Fragment, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';
import { useExchangeAndProgramNames, selectIsItuQuestionsDisabled, selectIfLivingArrangement } from '@reducers/appReducer';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';
import { preventerTools } from '@utils/tools/preventerTools';
import { questionsVisibility } from '@utils/miscellaneous/pageAndQuestionsVisibility';

// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';



/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Enrolled In Other Health Insurance
 *
 */
const Page4_66 = props => {

    // From Redux
    const {
        data,
        environment: {
            isState
        },
        form,
        household,
        householdMembers
    } = props;

    // From PagesContainer
    const {
        alwaysDisplayMedicaidQuestions,
        currentHhm,
        familyStatus,
        formName,
        hhmIndex: member,
        inlineCurrentIncomeEnabled,
    } = props;

    const collectLivingArrangementEnabled = useSelector(selectIfLivingArrangement);
    const isItuQuestionsDisabled = useSelector(selectIsItuQuestionsDisabled);
    const { exchangeMedicaid } = useExchangeAndProgramNames();

    const {
        applyingForCoverageIndicator,
        personId,
        healthCoverage: {
            absentParent,
            applicationSameAsPast3Months,
            eligibleITU,
            monthsRequestingAssistance: {
                lastMonth,
                lastButOneMonth,
                threeMonthsAgo
            },
            receivedITU,
            unpaidBill,
        },
        americanIndianAlaskaNative: {
            memberOfFederallyRecognizedTribeIndicator
        },
        name,
        inlineEligibility: {
            preliminaryMedicaidStatus,
            preliminaryEmergencyMedicaidStatus,
            preliminaryChipStatus,
        },
        livingArrangement,
        mcoSelection
    } = currentHhm;

    const {
        isFormSubmitted,
        isFormOnTopLevelValid
    } = preventerTools.provideObject(form[formName]);

    const onFieldChange = (...args) => {
        const { name, newData, value } = parseFieldChangeArgs(args);
        const newState = { ...data };

        if(name === 'unpaidBill' && value === false) {
            newData.healthCoverage.monthsRequestingAssistance = {
                lastMonth: null,
                lastButOneMonth: null,
                threeMonthsAgo: null
            };
            newData.healthCoverage.applicationSameAsPast3Months = null;
        }

        newState.taxHousehold.householdMember[member] = { ...newData };
        props.actions.changeData(newState);
    };

    const onFieldChange_needChildCoverage = (...args) => {
        const [ newData, indexInArray ] = args;

        const newState = { ...data };

        newState.taxHousehold.householdMember[indexInArray] = newData;

        newState.taxHousehold.householdMember[member].healthCoverage.coveredDependentChild =
            familyStatus.relatedPersonsIds.children.every(child => {
                const {
                    healthCoverage: {
                        childHaveCurrentCoverage
                    }
                } = householdMembers[child-1];

                return childHaveCurrentCoverage === true;
            });

        props.actions.changeData(newState);
    };

    const childrenHasHealthCoverage = (familyStatus) => {
        const {
            areChildrenShown,
            filteredChildren
        } = questionsVisibility.areChildrenHasHealthCoverageShown({
            alwaysDisplayMedicaidQuestions,
            familyStatus,
            household,
            householdMembers,
            inlineCurrentIncomeEnabled,
            personId,
            preliminaryMedicaidStatus
        });

        return areChildrenShown
            ? (
                <div className = 'grid-row gi-component gi-checkbox' >
                    <fieldset className = 'usa-fieldset' >
                        <legend className = 'required-false text-normal' >
                            <FormattedMessage id = 'ssap.input.legend.children_has_health_coverage' />
                        </legend>
                        <ul className = 'usa-list usa-list--unstyled' >
                            {
                                filteredChildren.map((child) => {

                                    const currentChild = householdMembers[child-1];

                                    const {
                                        applicantGuid,
                                        name,
                                        healthCoverage: {
                                            childHaveCurrentCoverage
                                        }
                                    } = currentChild;

                                    return (
                                        <li key = { applicantGuid } >
                                            <UniversalInput
                                                label = { {
                                                    text: normalizeNameOfPerson(name)
                                                } }
                                                inputData = { {
                                                    isInputRequired: false,
                                                    type: INPUT_TYPES.CHECKBOX,
                                                    name: 'childHaveCurrentCoverage',
                                                    classes: [ '' ],
                                                    checked: childHaveCurrentCoverage,
                                                    value: childHaveCurrentCoverage,
                                                    currentData: currentChild,
                                                    fields: [ 'healthCoverage' ],
                                                    isInputStandalone: true
                                                } }
                                                inputActions = { {
                                                    onFieldChange: onFieldChange_needChildCoverage
                                                } }
                                                indexInArray = { child-1 }
                                                errorClasses = { [ 'margin-l-0' ] }
                                            />
                                        </li>
                                    );
                                })
                            }
                        </ul>
                    </fieldset>
                </div>
            )
            : null;
    };

    const params = {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        collectLivingArrangementEnabled,
        familyStatus,
        memberOfFederallyRecognizedTribeIndicator,
        inlineCurrentIncomeEnabled,
        isItuQuestionsDisabled,
        isState,
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus,
        preliminaryChipStatus
    };

    const isAbsentParentShown = questionsVisibility.isMemberWithAbsentParentShown(params);
    const isIndianHealthServicesShown = questionsVisibility.isIndianHealthServicesShown(params);
    const isLivingArrangementShown = questionsVisibility.isLivingArrangementShown(params);
    const isMcoSelectionShown = questionsVisibility.isMcoSelectionShown(params);
    const isRetroactiveAssistanceShown = questionsVisibility.isRetroactiveAssistanceShown(params);

    useEffect(() => {
        if (isLivingArrangementShown && (!livingArrangement || livingArrangement === 'UNSPECIFIED')) {
            const newState = { ...data };
            newState.taxHousehold.householdMember[member] = {
                ...householdMembers[member],
                livingArrangement: 'HO'
            };
            props.actions.changeData(newState);
        }
    }, [isLivingArrangementShown, livingArrangement]);

    const replacements = {
        personName: (
            <strong>
                { normalizeNameOfPerson(name) }
            </strong>
        )
    };

    const checkTopLevelValidity = () => {
        if(isState.MN){
            if(unpaidBill){
                return [
                    lastMonth,
                    lastButOneMonth,
                    threeMonthsAgo
                ].some(v => v === true);
            } else {
                return true;
            }
        } else {
            return true;
        }
    };

    return (
        <Fragment>
            <Form
                { ...{
                    formName,
                    topLevelValidity: checkTopLevelValidity()
                } }
            >
                <div
                    className = 'fade-in'
                    id = 'aiStateHealthBenefit'
                >
                    <div className = 'subsection' >
                        {
                            isIndianHealthServicesShown &&

                            <Fragment>
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.eligible_for_tribal_program',
                                        legendTextReplacements: replacements
                                    } }
                                    label = { {
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'eligibleITU',
                                        value: eligibleITU,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: householdMembers[member],
                                        fields: [ 'healthCoverage' ],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.services_from_tribal_program',
                                        legendTextReplacements: replacements
                                    } }
                                    label = { {
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'receivedITU',
                                        value: receivedITU,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: householdMembers[member],
                                        fields: [ 'healthCoverage' ],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                            </Fragment>

                        }
                        {
                            isRetroactiveAssistanceShown  &&

                            <UniversalInput
                                legend = { {
                                    legendText: 'ssap.input.legend.non_internationalized_text',
                                    legendTextReplacements: {
                                        text: (
                                            <Fragment>
                                                <p className = 'required-true' >
                                                    <FormattedMessage
                                                        id = 'ssap.input.legend.medical_bills'
                                                        values = { replacements }
                                                    />
                                                </p>
                                                <FormattedMessage id = 'ssap.input.legend.medical_bills.note' />
                                            </Fragment>
                                        )
                                    }
                                } }
                                label = { {
                                    showRequiredIcon: false
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'unpaidBill',
                                    value: unpaidBill,
                                    options: DROPDOWN_OPTIONS.YES_NO,
                                    validationType: VALIDATION_TYPES.CHOSEN,
                                    currentData: householdMembers[member],
                                    fields: [ 'healthCoverage' ],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                            />
                        }
                        {
                            (isState.MN && unpaidBill)
                                ? (
                                    <Fragment>
                                        <ValidationErrorMessage
                                            isVisible = { isFormSubmitted && !isFormOnTopLevelValid }
                                            messageText = { VALIDATION_ERROR_MESSAGES.SPECIFIED }
                                            errorClasses = { [ 'margin-l-0' ] }
                                        />

                                        <p className = 'required-true' >
                                            <FormattedMessage id = 'ssap.additional_info.select_months_with_medical_bills' />
                                        </p>

                                        <ul className = 'usa-list usa-list--unstyled' >
                                            <li>
                                                <UniversalInput
                                                    label = { {
                                                        text: 'ssap.input.label.medical_bills_last_month',
                                                    } }
                                                    inputData = { {
                                                        isInputRequired: false,
                                                        type: INPUT_TYPES.CHECKBOX,
                                                        name: 'lastMonth',
                                                        classes: [''],
                                                        checked: lastMonth,
                                                        value: lastMonth,
                                                        currentData: householdMembers[member],
                                                        fields: [ 'healthCoverage', 'monthsRequestingAssistance' ],
                                                        isInputStandalone: true
                                                    } }
                                                    inputActions = { {
                                                        onFieldChange: onFieldChange
                                                    } }
                                                />
                                                <UniversalInput
                                                    label = { {
                                                        text: 'ssap.input.label.medical_bills_last_but_one_month',
                                                    } }
                                                    inputData = { {
                                                        isInputRequired: false,
                                                        type: INPUT_TYPES.CHECKBOX,
                                                        name: 'lastButOneMonth',
                                                        classes: [''],
                                                        checked: lastButOneMonth,
                                                        value: lastButOneMonth,
                                                        currentData: householdMembers[member],
                                                        fields: [ 'healthCoverage', 'monthsRequestingAssistance' ],
                                                        isInputStandalone: true
                                                    } }
                                                    inputActions = { {
                                                        onFieldChange: onFieldChange
                                                    } }
                                                />
                                                <UniversalInput
                                                    label = { {
                                                        text: 'ssap.input.label.medical_bills_three_months_ago',
                                                    } }
                                                    inputData = { {
                                                        isInputRequired: false,
                                                        type: INPUT_TYPES.CHECKBOX,
                                                        name: 'threeMonthsAgo',
                                                        classes: [''],
                                                        checked: threeMonthsAgo,
                                                        value: threeMonthsAgo,
                                                        currentData: householdMembers[member],
                                                        fields: [ 'healthCoverage', 'monthsRequestingAssistance' ],
                                                        isInputStandalone: true
                                                    } }
                                                    inputActions = { {
                                                        onFieldChange: onFieldChange
                                                    } }
                                                />
                                            </li>
                                        </ul>

                                        <UniversalInput
                                            legend = { {
                                                legendText: 'ssap.input.legend.application_same_as_past_3_months',
                                                legendTextReplacements: { exchangeMedicaid }
                                            } }
                                            label = { {
                                                showRequiredIcon: true
                                            } }
                                            inputData = { {
                                                isInputRequired: true,
                                                type: INPUT_TYPES.RADIO_BUTTONS,
                                                name: 'applicationSameAsPast3Months',
                                                value: applicationSameAsPast3Months,
                                                options: DROPDOWN_OPTIONS.YES_NO,
                                                validationType: VALIDATION_TYPES.CHOSEN,
                                                currentData: householdMembers[member],
                                                fields: [ 'healthCoverage' ],
                                                isInputStandalone: true
                                            } }
                                            inputActions = { {
                                                onFieldChange: onFieldChange
                                            } }
                                            errorClasses = { [ 'margin-l-0' ] }
                                        />
                                    </Fragment>
                                )
                                : null
                        }
                        {
                            childrenHasHealthCoverage(familyStatus)
                        }
                        {
                            isAbsentParentShown &&

                            <UniversalInput
                                legend = { {
                                    legendText: 'ssap.input.legend.parents_live_outside',
                                    legendTextReplacements: replacements
                                } }
                                label = { {
                                    showRequiredIcon: true
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'absentParent',
                                    value: absentParent,
                                    options: DROPDOWN_OPTIONS.YES_NO,
                                    validationType: VALIDATION_TYPES.CHOSEN,
                                    currentData: householdMembers[member],
                                    fields: [ 'healthCoverage' ],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                            />
                        }
                        {
                            isMcoSelectionShown &&

                            <UniversalInput
                                legend = { {
                                    dataInstructions: 'instructional_text.mco_selection',
                                    legendText: 'ssap.input.legend.mco_selection',
                                    legendTextReplacements: replacements
                                } }
                                label = { {
                                    showRequiredIcon: true
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'mcoSelection',
                                    value: mcoSelection,
                                    options: DROPDOWN_OPTIONS.MCO_SELECTIONS,
                                    validationType: VALIDATION_TYPES.NOT_UNSPECIFIED,
                                    currentData: householdMembers[member],
                                    fields: [ ],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                            />
                        }
                        {
                            isLivingArrangementShown &&

                            <UniversalInput
                                label = { {
                                    text: 'ssap.input.label.living_arrangement_question',
                                    labelTextReplacements: replacements,
                                    labelClass: [ 'text-left', 'w-100', 'p-0', 'mb-2' ],
                                    showRequiredIcon: true
                                } }
                                dropdownContainerClassName = 'd-flex flex-column align-items-start'
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.DROPDOWN,
                                    name: 'livingArrangement',
                                    value: (livingArrangement && livingArrangement !== 'UNSPECIFIED') ? livingArrangement : 'HO',
                                    options: DROPDOWN_OPTIONS.LIVING_ARRANGEMENT.filter(opt => opt.value !== 'default'),
                                    valuesToMatch: DROPDOWN_OPTIONS.LIVING_ARRANGEMENT_CODES,
                                    validationType: VALIDATION_TYPES.CHOSEN_FROM_LIST,
                                    currentData: householdMembers[member],
                                    fields: [ ],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                            />
                        }
                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    data: state.ssap.data,
    form: state.ssap.form,

    household: state
        .ssap
        .data
        .taxHousehold,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page4_66);
